import { yupResolver } from '@hookform/resolvers/yup'
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import { selectPatientOrder } from '@state/reducers/orderReducer'
import { DatePicker } from '@mui/lab'
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  Grid,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Radio,
  RadioGroup,
  Stack,
  SxProps,
  TextField,
  Theme,
  Typography,
} from '@mui/material'
import { DataGridPro, GridColumns } from '@mui/x-data-grid-pro'
import { ExternalId, PatientDto } from '@services/api'
import { setPatientsFilter } from '@state/reducers/patientsReducer'
import { fetchPatients } from '@state/thunks/patientsThunk'
import { patientSearchSchema } from '@utils/schemas'
import { X } from 'lucide-react'
import moment from 'moment'
import React, { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { PatientInit } from 'src/containers/app/Worklists/DialogPatientFusion'
import { useIntl } from 'react-intl'
import { formatSsn, formatSsnPrefix } from '@utils/ssnUtil'

type Props = {
  multipleSelection?: boolean
  style?: SxProps<Theme>
  onConfirm?: (data: PatientDto | PatientDto[]) => void
  selectedRows?: PatientDto[]
  onClose?: () => void
  patientToSearch?: PatientDto | null
  patients: PatientDto[]
  patientsToBeMerged?: PatientDto[]
}

// TODO add pagination to the table
export const PatientTable: React.FC<Props> = ({
  multipleSelection,
  selectedRows,
  onConfirm,
  style,
  patients,
  patientsToBeMerged,
}) => {
  const intl = useIntl()
  const selectedPatientOrder = useAppSelector(selectPatientOrder)
  const { getValues, control, reset, setValue } = useForm<PatientInit>({
    resolver: yupResolver(patientSearchSchema),
  })
  const [isLoading, setIsLoading] = useState(false)
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (selectedPatientOrder && multipleSelection) {
      setValue('firstName', selectedPatientOrder.firstName || '')
      setValue('lastName', selectedPatientOrder.lastName || '')

      handleApplyFilters()
    }
  }, [selectedPatientOrder, multipleSelection])

  const columns: GridColumns = useMemo(() => {
    const selectedRowIds = selectedRows?.map((row) => row.id) || []

    let baseColumns = [
      {
        headerName: 'ID',
        field: 'id',
        width: 50,
      },
      {
        headerName: intl.formatMessage({ id: 'externalId' }),
        field: 'external_id',
        width: 150,
        valueFormatter: ({ value }) => {
          return (value as ExternalId[]).map((eid) => eid.value).join(',')
        },
      },
      {
        headerName: intl.formatMessage({ id: 'gender' }),
        field: 'sex',
        width: 80,
      },
      {
        headerName: intl.formatMessage({ id: 'lastName' }),
        field: 'lastName',
        width: 150,
      },
      {
        headerName: intl.formatMessage({ id: 'firstName' }),
        field: 'firstName',
        width: 150,
      },
      {
        headerName: intl.formatMessage({ id: 'birthDate' }),
        field: 'birthDate',
        width: 160,
        valueFormatter: ({ value }) => {
          return moment(value as string).format('DD/MM/YYYY')
        },
      },
      {
        field: 'ssn',
        headerName: 'Numéro de sécurité sociale',
        width: 220,
        valueFormatter: ({ value }: { value: string }) =>
          `${
            value
              ? formatSsnPrefix(value.slice(0, 7)) +
                ' ' +
                formatSsn(value.slice(7, 15))
              : ''
          }`,
      },
      {
        headerName: intl.formatMessage({ id: 'address' }),
        field: 'address',
        width: 160,
      },
      {
        headerName: intl.formatMessage({ id: 'createdAt' }),
        field: 'createdAt',
        width: 160,
        valueFormatter: ({ value }) => {
          return moment(value as string).format('DD/MM/YYYY HH:mm:ss')
        },
      },
      {
        headerName: intl.formatMessage({ id: 'phoneNumber' }),
        field: 'phoneNumber',
        width: 160,
      },
      {
        field: 'placeOfBirth',
        headerName: 'Lieu de naissance',
        width: 160,
      },
    ] as GridColumns

    baseColumns = baseColumns.concat([
      {
        headerName: 'INS',
        field: 'patient_ins_history',
        width: 160,
        valueGetter: (params) => {
          const row: PatientDto = params.row
          return row.patient_ins_history?.[0]?.ins_value
        },
      },
    ])

    // Add Action column for both display and selection modes when selection is enabled
    if (onConfirm) {
      baseColumns = baseColumns.concat([
        {
          headerName: 'Action',
          field: 'action',
          align: 'right',
          width: 160,
          renderCell: (params) => {
            const isSelected = selectedRowIds.includes(params.row.id)
            return (
              <Button
                variant="contained"
                onClick={() => {
                  if (!!onConfirm && !!selectedRows) {
                    if (isSelected) {
                      onConfirm(
                        multipleSelection
                          ? selectedRows.filter(
                              (row) => row.id !== params.row.id,
                            )
                          : [],
                      )
                    } else {
                      onConfirm(
                        multipleSelection
                          ? [...selectedRows, params.row]
                          : [params.row],
                      )
                    }
                  }
                }}
                style={{
                  color: isSelected ? 'white' : 'black',
                  backgroundColor: isSelected ? '#ff7675' : '#55efc4',
                  fontWeight: 'bold',
                }}
              >
                {isSelected
                  ? intl.formatMessage({ id: 'unselect' })
                  : intl.formatMessage({ id: 'select' })}
              </Button>
            )
          },
        },
      ])
    }
    return baseColumns
  }, [selectedRows, multipleSelection, onConfirm, intl])

  const handleApplyFilters = () => {
    setIsLoading(true)

    dispatch(
      setPatientsFilter({
        ...getValues(),
        id:
          getValues().id !== null && getValues().id !== undefined
            ? Number(getValues().id)
            : undefined,
        birthDate: getValues().birthDate
          ? new Date(getValues().birthDate!)
          : undefined,
        sex: getValues().sex as 'F' | 'M',
      }),
    )
    dispatch(fetchPatients())
      .unwrap()
      .finally(() => setIsLoading(false))
  }

  const renderPatientFilters = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          marginBottom: 2,
        }}
      >
        {patientsToBeMerged && patientsToBeMerged.length !== 0 && (
          <Box
            sx={{
              width: '35%',
              backgroundColor: 'background.paper',
              borderRadius: 2,
              boxShadow: 1,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              height: 300,
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.5,
                backgroundColor: 'primary.main',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography variant="subtitle2" fontWeight={600}>
                Patients à fusionner
              </Typography>
              <Chip
                label={patientsToBeMerged.length}
                size="small"
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                }}
              />
            </Box>

            <List dense sx={{ flex: 1, overflow: 'auto', py: 0 }}>
              {patientsToBeMerged.map((patient, index) => (
                <ListItem
                  key={patient.id}
                  sx={{
                    borderBottom: '1px solid',
                    borderColor: 'grey.100',
                    backgroundColor: index === 0 ? 'primary.50' : 'transparent',
                  }}
                >
                  <ListItemText
                    primary={
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="body2" fontWeight={500}>
                          {patient.firstName} {patient.lastName} - {patient.sex}{' '}
                          - {patient.ssn}
                        </Typography>
                      </Stack>
                    }
                    secondary={new Date(patient.birthDate).toLocaleDateString(
                      'fr-FR',
                      {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      },
                    )}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
        <Box
          sx={{
            width: patientsToBeMerged?.length !== 0 ? '65%' : '100%',
            backgroundColor: 'background.paper',
            borderRadius: 2,
            px: 2,
            py: 1.5,
            boxShadow: 1,
          }}
        >
          <Grid container rowSpacing={2} spacing={1}>
            <Grid item xs={12}>
              <Controller
                name="sex"
                control={control}
                render={({ field }) => (
                  <RadioGroup
                    row
                    aria-labelledby="sexe"
                    {...field}
                    value={field.value ?? ''}
                  >
                    <FormControlLabel
                      style={{ color: '#48bef9', fontWeight: 'bold' }}
                      value="M"
                      label="Homme"
                      control={
                        <Radio
                          sx={{
                            color: '#48bef9',
                            '&.Mui-checked': { color: '#48bef9' },
                          }}
                        />
                      }
                    />
                    <FormControlLabel
                      value="F"
                      style={{ color: '#f48dde', fontWeight: 'bold' }}
                      label="Femme"
                      control={
                        <Radio
                          sx={{
                            color: '#f48dde',
                            '&.Mui-checked': { color: '#f48dde' },
                          }}
                        />
                      }
                    />
                  </RadioGroup>
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="id"
                control={control}
                render={({ field }) => (
                  <TextField
                    size="small"
                    id="idA"
                    type="number"
                    label="ID"
                    fullWidth
                    {...field}
                    value={field.value?.toString() ?? ''}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="lastName"
                control={control}
                render={({ field }) => (
                  <TextField
                    size="small"
                    id="lastNameA"
                    label="Nom (ou nom de naissance)"
                    fullWidth
                    {...field}
                    value={field.value ?? ''}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="firstName"
                control={control}
                render={({ field }) => (
                  <TextField
                    size="small"
                    id="firstNameA"
                    label="Prénom"
                    fullWidth
                    {...field}
                    value={field.value ?? ''}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="birthDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    value={field.value ? new Date(field.value) : null}
                    label="Date de naissance"
                    renderInput={(props) => (
                      <TextField size="small" fullWidth {...props} />
                    )}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="ipp"
                control={control}
                render={({ field }) => (
                  <TextField
                    size="small"
                    id="ippA"
                    label="IPP"
                    fullWidth
                    {...field}
                    value={field.value ?? ''}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="originIpp"
                control={control}
                render={({ field }) => (
                  <TextField
                    size="small"
                    id="originIppA"
                    label="IPP Origine"
                    fullWidth
                    {...field}
                    value={field.value ?? ''}
                  />
                )}
              />
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<X size={16} />}
              onClick={() => reset()}
            >
              Effacer les filtres
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleApplyFilters}
            >
              Rechercher
            </Button>
          </Box>
        </Box>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        borderRadius: 2,
        padding: 2,
        boxShadow: 2,
        flex: 1,
        overflow: 'hidden',
      }}
    >
      <div>{renderPatientFilters()}</div>
      <DataGridPro
        rows={patients}
        getRowId={(row) => row.id}
        columns={columns}
        sx={{
          border: 'none',
          ...style,
        }}
        {...(multipleSelection && {
          selectionModel: selectedRows?.map((row) => row.id) || [],
        })}
        disableMultipleSelection
        pinnedColumns={{
          right: ['action'],
        }}
        components={{
          LoadingOverlay: LinearProgress,
        }}
        loading={isLoading}
      />
    </Box>
  )
}
